import { makeStyles } from 'tss-react/mui';

export const useSplitsViewStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.border.light}`,
    boxShadow: theme.shadows[7],
    borderRadius: 12,
    flex: '1 1 auto',
    // Without this the card's min-width resolves to the widest split row's min-content (the
    // no-wrap category/account name), so it refuses to shrink and overflows a narrow container.
    minWidth: 0,
  },
  rootSeries: {
    boxShadow: theme.shadows[1],
    width: '100%',
  },
  titleIconGroup: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    padding: theme.spacing(1.5, 2, 1),
    borderBottom: `1px solid ${theme.palette.border.main}`,
  },
  titleIconGroupSeries: {
    justifyContent: 'space-between',
    padding: theme.spacing(1, 2),
    borderBottom: `1px solid ${theme.palette.border.light}`,
  },
  titleWithIcon: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  repeatIcon: {
    height: 18,
    width: 18,
    color: theme.palette.icon.light,
  },
  toggleButtons: {
    color: theme.palette.primary.main,
    borderRadius: 22,
    marginRight: -14,
  },
  editButton: {
    padding: '6px 8px',
    minWidth: 'unset',
    textTransform: 'none',
    ...theme.typography.paragraphMedium,
  },
  preview: {
    width: '100%',
    textAlign: 'left',
    fontWeight: 500,
  },
  /** Outer split row (transaction detail): vertical stack of line 1 / tags / memo */
  rowRootDetail: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing(1),
  },
  splitRow: {
    display: 'flex',
    flexDirection: 'column',
  },
  /** Series: same vertical structure, padding from container */
  splitItemSeriesBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing(1),
  },
  /**
   * Row 1: Category (+ transfer) | Client & project | Exclusions + amount, billable below amount.
   * Grid collapses to one column on small screens.
   */
  splitRowLine1: {
    display: 'grid',
    gap: theme.spacing(2),
    alignItems: 'start',
    width: '100%',
    minWidth: 0,
  },
  splitRowLine1ThreeCol: {
    gridTemplateColumns: 'minmax(0, 1fr) 240px 140px',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
    },
  },
  /** QBP: Personal/Business usage icon is its own leading column. */
  splitRowLine1ThreeColQbp: {
    gap: theme.spacing(1),
    gridTemplateColumns: 'auto minmax(0, 1fr) 240px 140px',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
    },
  },
  splitRowLine1TwoCol: {
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
    },
  },
  splitRowLine1TwoColQbp: {
    gap: theme.spacing(1),
    gridTemplateColumns: 'auto minmax(0, 1fr) auto',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: '1fr',
    },
  },
  splitRowUsageCol: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    flexShrink: 0,
  },
  splitRowCategoryCol: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  splitRowCategoryStack: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(0.5),
    minWidth: 0,
    flex: '1 1 auto',
  },
  categoryText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
  },
  splitRowClientProjectCol: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 0,
    minWidth: 0,
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  splitRowAmountCol: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: theme.spacing(0.5),
    justifySelf: 'end',
    minWidth: 0,
    [theme.breakpoints.down('md')]: {
      width: '100%',
      alignItems: 'flex-end',
    },
  },
  splitRowAmountInline: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    flexWrap: 'nowrap',
  },
  /** Row 2: tags only */
  splitRowTags: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    width: '100%',
    minWidth: 0,
    paddingLeft: 0,
  },
  splitRowTagsQbp: {
    paddingLeft: theme.spacing(3),
  },
  /** Row 3: memo + expand */
  splitRowMemo: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: theme.spacing(0.5),
    width: '100%',
    minWidth: 0,
    paddingLeft: 0,
  },
  splitRowMemoQbp: {
    paddingLeft: theme.spacing(3),
  },
  memoText: {
    flex: '1 1 auto',
    minWidth: 0,
  },
  memoToggle: {
    alignSelf: 'flex-start',
    minWidth: 'max-content',
    padding: theme.spacing(0, 0, 0, 0.5),
    textTransform: 'none',
    fontSize: theme.typography.paragraphRegular.fontSize,
    fontWeight: theme.typography.fontWeightRegular,
    lineHeight: theme.typography.paragraphRegular.lineHeight,
  },
  /** Drawer: note text + toggle stacked, so the toggle sits below the note (flush-left). */
  memoBody: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    minWidth: 0,
  },
  memoToggleBelow: {
    alignSelf: 'flex-start',
    minWidth: 'max-content',
    padding: 0,
    textTransform: 'none',
    fontSize: theme.typography.paragraphRegular.fontSize,
    fontWeight: theme.typography.fontWeightRegular,
    lineHeight: theme.typography.paragraphRegular.lineHeight,
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  exclusionCluster: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    padding: theme.spacing(0.25, 0.5),
    borderRadius: 6,
    backgroundColor: theme.palette.background.default,
  },
  billableChip: {
    height: 24,
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
    borderRadius: 6,
    backgroundColor: theme.palette.pending.light,
    color: theme.palette.pending.dark,
    '& .MuiChip-label': {
      fontSize: theme.typography.captionRegular.fontSize,
      lineHeight: 1.2,
      paddingLeft: theme.spacing(0.25),
      paddingRight: theme.spacing(0.25),
    },
    '& .MuiChip-icon': {
      marginLeft: 0,
      marginRight: 0,
      color: theme.palette.pending.dark,
    },
  },
  chip: {
    height: 28,
    maxWidth: 160,
    minWidth: 40,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.default,
    borderRadius: 22,
    border: 'none',
  },
  inputField: {
    fontSize: theme.typography.paragraphRegular.fontSize,
    textAlign: 'right',
  },
  overflowAmount: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(0.75),
    color: theme.palette.text.secondary,
  },
  padding: {
    display: 'flex',
    padding: 0.5,
    flexShrink: 0,
  },
  infoIcon: {
    color: theme.palette.icon.light,
    marginLeft: theme.spacing(1),
    height: 18,
    width: 18,
  },
  excludeIcon: {
    height: theme.spacing(3),
    width: theme.spacing(3),
  },
  exclusionIconSmall: {
    width: 16,
    height: 16,
    display: 'block',
  },
  splitPadding: {
    padding: theme.spacing(0, 1),
  },
  showAllButton: {
    width: '100%',
    padding: theme.spacing(1, 0),
  },

  // ===== Drawer layout: single-column stack of icon-prefixed sections (tags / clients & =====
  // ===== projects / note) instead of the modal's multi-column grid. Drawer-only.        =====
  /** Vertical stack: primary row, then optional tag / client-project / note rows. */
  drawerSplitRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    width: '100%',
    minWidth: 0,
  },
  /** Avatar + category stack on the left, amount + billable on the right. */
  drawerPrimaryRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    width: '100%',
    minWidth: 0,
  },
  drawerPrimaryLeft: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    minWidth: 0,
    flex: '1 1 auto',
  },
  drawerCategoryStack: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(0.5),
    minWidth: 0,
    flex: '1 1 auto',
  },
  drawerAmountCol: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: theme.spacing(0.5),
    flexShrink: 0,
  },
  /** Icon-prefixed metadata row (tags / clients & projects). */
  drawerMetaRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    width: '100%',
    minWidth: 0,
  },
  drawerMetaIcon: {
    fontSize: 20,
    flexShrink: 0,
    // icon/light (#617179) — matches the filled label / people glyphs in the drawer spec.
    color: theme.palette.icon.light,
  },
  drawerTagsWrap: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  /** Chip sized to the drawer spec (h22, rounded 8) rather than the modal chip (h28, pill). */
  drawerTagChip: {
    height: 22,
    maxWidth: 160,
    borderRadius: 8,
    border: 'none',
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.default,
    '& .MuiChip-label': {
      ...theme.typography.captionRegular,
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
  },
  drawerClientProjectText: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    columnGap: theme.spacing(0.5),
    minWidth: 0,
  },
}));
